/**
 * ChineseSymbolsHandler
 * ---------------------
 * This utility class provides automatic Chinese typesetting adjustments for web pages.
 * It scans text nodes for Chinese opening, closing, and separator symbols, and wraps them
 * with span tags that apply negative margins for improved typographic appearance.
 *
 * Features:
 * - Adds margin-left for opening symbols and margin-right for closing symbols.
 * - Adds both margins for separator symbols found between opening/closing symbols.
 * - Prevents double-wrapping and preserves original text structure.
 * - Injects required CSS styles automatically.
 *
 * Usage:
 * Instantiate and call checkPageForSymbols() after page content is loaded.
 */

export default class ChineseSymbolsHandler {
  constructor() {
    this.openingSymbols = ["（", "【", "《", "「", "『"];
    this.closingSymbols = ["）", "】", "》", "」", "』"];
    this.separatorSymbols = ["，", "。", "！", "？", "；", "：", "、"];
    this.injectCSS();
  }

  injectCSS() {
    // Check if style already exists
    if (!document.querySelector("#ch-char-offset-style")) {
      const style = document.createElement("style");
      style.id = "ch-char-offset-style";
      style.textContent = `
        .ch-char-offset-l {
          margin-left: -0.5em;
        }
        .ch-char-offset-r {
          margin-right: -0.5em;
        }
      `;
      document.head.appendChild(style);
    }
  }

  checkPageForSymbols() {
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          if (!node.nodeValue || !node.nodeValue.trim())
            return NodeFilter.FILTER_REJECT;
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          const tag = parent.tagName.toLowerCase();
          if (["script", "style", "textarea", "input"].includes(tag))
            return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      },
      false
    );

    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) {
      textNodes.push(node);
    }

    textNodes.forEach((textNode) => {
      const rawText = textNode.nodeValue;
      let newHtml = "";
      let i = 0;
      let hasSymbol = false;

      while (i < rawText.length) {
        let wrapped = false;

        // Opening symbols
        for (const symbol of this.openingSymbols) {
          if (rawText.startsWith(symbol, i)) {
            hasSymbol = true;
            if (i === 0 || /\s$/.test(rawText[i - 1])) {
              newHtml += `<span class="ch-char-offset-l">${symbol}</span>`;
            } else {
              newHtml += `&nbsp;<span class="ch-char-offset-l">${symbol}</span>`;
            }
            i += symbol.length;
            wrapped = true;
            break;
          }
        }
        if (wrapped) continue;

        // Closing symbols
        for (const symbol of this.closingSymbols) {
          if (rawText.startsWith(symbol, i)) {
            hasSymbol = true;
            newHtml += `<span class="ch-char-offset-r">${symbol}</span>&nbsp;`;
            i += symbol.length;
            wrapped = true;
            break;
          }
        }
        if (wrapped) continue;

        // Separator symbols
        let sepWrapped = false;
        for (const symbol of this.separatorSymbols) {
          if (rawText.startsWith(symbol, i)) {
            const prevIsSymbol =
              i > 0 &&
              (this.openingSymbols.includes(rawText[i - 1]) ||
                this.closingSymbols.includes(rawText[i - 1]));
            const nextIsSymbol =
              i < rawText.length - 1 &&
              (this.openingSymbols.includes(rawText[i + 1]) ||
                this.closingSymbols.includes(rawText[i + 1]));
            if (prevIsSymbol && nextIsSymbol) {
              hasSymbol = true;
              newHtml += `&nbsp;<span class="ch-char-offset-l ch-char-offset-r">${symbol}</span>&nbsp;`;
              i += symbol.length;
              sepWrapped = true;
              break;
            }
          }
        }
        if (sepWrapped) continue;

        // Regular character
        // Escape '<' to avoid accidental HTML injection when creating fragment
        const ch = rawText[i] === "<" ? "&lt;" : rawText[i];
        newHtml += ch;
        i++;
      }

      if (hasSymbol) {
        const frag = document.createRange().createContextualFragment(newHtml);
        textNode.parentNode.replaceChild(frag, textNode);
      }
    });
  }
}
